import { useOptionalDataLayerVisibility } from '@/composables/scopedStores/layers/useDataLayerVisibility'
import useLanguage, { useInlineLanguage } from '@/composables/useLanguage'
import { getMetadataByName, metadataFor } from '@/utils/layers/metadata'
import {
  IBaseDataLayer,
  ILayerValue,
  IMetadataAttribute,
  TLayerMetadata
} from '@lifk/types'
import { computed, Ref } from 'vue'

/*
  Ref arguments must be ComputedRefs to work.
  toRef() reactivity sometimes breaks when switching between layers,
    but ComputedRefs always keep their reactivity.
*/
export default (
  dataLayer: Ref<IBaseDataLayer | undefined>,
  layerValue?: Ref<ILayerValue>
) => {
  const i18n = useLanguage('layerUnits')
  const { t } = useInlineLanguage()
  const fetchAttempted: string[] = []

  // Must be optional, because this is used within Tooltips (outside of map context)
  const dataLayerVisibilityStore = useOptionalDataLayerVisibility()

  const isPercentile = computed(
    () => dataLayer.value?.layerType === 'metric (percentile)'
  )

  const metricLayerValue = computed<ILayerValue | undefined>(() => {
    if (layerValue?.value) {
      return layerValue.value
    }

    const isMetricLayer =
      dataLayer.value?.layerType &&
      dataLayer.value?.layerType.startsWith('metric')

    if (dataLayer.value && isMetricLayer) {
      // find the first layer value that is a metric with a targetAttribute
      //   (all *metric* layer values should have the same targetAttr within a single layer,
      //   but there may also be *feature* layer values that should be skipped here.)
      return dataLayer.value.mapLayers.find(({ layerValues }) =>
        layerValues.some(
          (lv) => isMetricLayer && !!lv.styleConfig.targetAttribute
        )
      )?.layerValues[0]
    }

    return undefined
  })

  const targetAttrMetadata = computed<
    { displayName: string; unit?: string } | undefined
  >(() => {
    return metricLayerValue.value && dataLayerVisibilityStore
      ? getAttributeMetadata(
          metricLayerValue.value.key,
          metricLayerValue.value.styleConfig.targetAttribute,
          dataLayerVisibilityStore.loadedMetadata.value,
          dataLayer.value
        )
      : undefined
  })

  const layerValueDisplayName = computed<string>(() => {
    // if target attribute and NOT a Raster, use metadata displayName
    if (
      metricLayerValue.value?.style !== 'Raster' &&
      targetAttrMetadata.value?.displayName
    ) {
      return targetAttrMetadata.value.displayName
    }
    if (metricLayerValue.value) {
      return t(metricLayerValue.value.name)
    }
    // default to data layer name
    if (dataLayer.value) {
      return t(dataLayer.value.name)
    }
    return ''
  })

  const targetAttrUnitStr = computed<string | undefined>(() => {
    if (
      targetAttrMetadata.value?.unit &&
      targetAttrMetadata.value.unit.length > 0
    ) {
      return (
        i18n.value[targetAttrMetadata.value.unit] ??
        targetAttrMetadata.value.unit
      )
    }
    return undefined
  })

  const getAttributeMetadata = (
    layerValueKey: string,
    attributeName: string | undefined,
    loadedMetadata: TLayerMetadata,
    dataLayer?: IBaseDataLayer
  ): IMetadataAttribute | undefined => {
    if (
      attributeName &&
      attributeName !== 'color' // for tribal areas
    ) {
      const metadata = metadataFor('tileset', layerValueKey, loadedMetadata)
      if (metadata.length) {
        return getMetadataByName(metadata, attributeName)
      } else if (dataLayer && !fetchAttempted.includes(dataLayer.key)) {
        // load metadata if data layer argument provided
        // This allows load of metadata for data layers that are not loaded on the map,
        //   eg- for Reference articles.

        dataLayerVisibilityStore?.updateMetadata([dataLayer])

        // This prevents an infinite loop if metadata does not exist for the layer
        fetchAttempted.push(dataLayer.key)
      }
    }
    return undefined
  }

  return {
    layerValueDisplayName,
    targetAttrMetadata,
    targetAttrUnitStr,
    isPercentile
  }
}
